import React, { useState, useEffect, useCallback } from 'react';
import gql from 'graphql-tag';
import client from '../../lib/apolloClient';
import {
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import SaveIcon from '@material-ui/icons/Save';
import DeleteIcon from '@material-ui/icons/Delete';
import UpdateIcon from '@material-ui/icons/Refresh';
import StarIcon from '@material-ui/icons/Star';

const styles = theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
  },
  formControl: {
    minWidth: 220,
  },
  createdBy: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
  },
  defaultStar: {
    color: '#f9a825',
    fontSize: '1rem',
    marginLeft: 4,
    verticalAlign: 'middle',
  },
  dialogContent: {
    minWidth: 400,
  },
});

const SAVED_FILTER_SETS_QUERY = gql`
  query SavedFilterSets($filterContext: String) {
    savedFilterSets(filterContext: $filterContext) {
      id
      name
      filters
      filter_context
      is_default
      created_by_name
    }
  }
`;

const CREATE_SAVED_FILTER_SET = gql`
  mutation CreateSavedFilterSet(
    $name: String!
    $filters: JSON!
    $filterContext: String
    $isDefault: Boolean
  ) {
    create_saved_filter_set(
      name: $name
      filters: $filters
      filterContext: $filterContext
      isDefault: $isDefault
    ) {
      saved_filter_set {
        id
        name
        filters
        filter_context
        is_default
        created_by_name
      }
      errors
    }
  }
`;

const UPDATE_SAVED_FILTER_SET = gql`
  mutation UpdateSavedFilterSet(
    $id: ID!
    $name: String
    $filters: JSON
    $isDefault: Boolean
  ) {
    update_saved_filter_set(
      id: $id
      name: $name
      filters: $filters
      isDefault: $isDefault
    ) {
      saved_filter_set {
        id
        name
        filters
        filter_context
        is_default
        created_by_name
      }
      errors
    }
  }
`;

const DELETE_SAVED_FILTER_SET = gql`
  mutation DeleteSavedFilterSet($id: ID!) {
    delete_saved_filter_set(id: $id) {
      success
      errors
    }
  }
`;

const SavedFilterSetSelector = ({
  classes,
  filterContext = 'user_admin',
  currentFilterList,
  nameSearch,
  distanceSearch,
  distanceWithin,
  onApplyFilterSet,
}) => {
  const [selectedId, setSelectedId] = useState('');
  const [filterSets, setFilterSets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchFilterSets = useCallback(() => {
    setLoading(true);
    client
      .query({
        query: SAVED_FILTER_SETS_QUERY,
        variables: { filterContext },
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        setFilterSets((data && data.savedFilterSets) || []);
      })
      .catch(() => {
        setFilterSets([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [filterContext]);

  useEffect(() => {
    fetchFilterSets();
  }, [fetchFilterSets]);

  useEffect(() => {
    if (filterSets.length > 0 && !selectedId) {
      const defaultSet = filterSets.find(fs => fs.is_default);
      if (defaultSet) {
        setSelectedId(defaultSet.id);
      }
    }
  }, [filterSets]);

  const selectedFilterSet = filterSets.find(fs => String(fs.id) === String(selectedId));

  const buildFiltersPayload = () => {
    const payload = { filter_list: currentFilterList || {} };
    if (nameSearch) payload.name_search = nameSearch;
    if (distanceSearch) payload.distance_search = distanceSearch;
    if (distanceWithin) payload.distance_within = distanceWithin;
    return payload;
  };

  const handleSave = () => {
    if (!newName.trim()) return;

    setErrorMessage('');
    setMutating(true);
    client
      .mutate({
        mutation: CREATE_SAVED_FILTER_SET,
        variables: {
          name: newName.trim(),
          filters: buildFiltersPayload(),
          filterContext,
          isDefault: isDefault,
        },
      })
      .then(({ data: result }) => {
        const errors = (result && result.create_saved_filter_set && result.create_saved_filter_set.errors) || [];
        if (errors.length > 0) {
          setErrorMessage(errors.join(', '));
          return;
        }
        const created = result && result.create_saved_filter_set && result.create_saved_filter_set.saved_filter_set;
        if (created) {
          setSelectedId(created.id);
        }
        setSaveDialogOpen(false);
        setNewName('');
        setIsDefault(false);
        fetchFilterSets();
      })
      .finally(() => {
        setMutating(false);
      });
  };

  const handleUpdate = () => {
    if (!selectedFilterSet) return;

    setErrorMessage('');
    setMutating(true);
    client
      .mutate({
        mutation: UPDATE_SAVED_FILTER_SET,
        variables: {
          id: selectedFilterSet.id,
          filters: buildFiltersPayload(),
        },
      })
      .then(({ data: result }) => {
        const errors = (result && result.update_saved_filter_set && result.update_saved_filter_set.errors) || [];
        if (errors.length > 0) {
          setErrorMessage(errors.join(', '));
          return;
        }
        fetchFilterSets();
      })
      .finally(() => {
        setMutating(false);
      });
  };

  const handleDelete = () => {
    if (!selectedFilterSet) return;

    setErrorMessage('');
    setMutating(true);
    client
      .mutate({
        mutation: DELETE_SAVED_FILTER_SET,
        variables: { id: selectedFilterSet.id },
      })
      .then(({ data: result }) => {
        const errors = (result && result.delete_saved_filter_set && result.delete_saved_filter_set.errors) || [];
        if (errors.length > 0) {
          setErrorMessage(errors.join(', '));
          return;
        }
        setSelectedId('');
        setDeleteDialogOpen(false);
        fetchFilterSets();
      })
      .finally(() => {
        setMutating(false);
      });
  };

  const handleApply = () => {
    if (!selectedFilterSet) return;
    onApplyFilterSet(selectedFilterSet.filters);
  };

  const handleSelectChange = event => {
    setSelectedId(event.target.value);
  };

  const isBusy = loading || mutating;

  return (
    <div className={classes.root}>
      <FormControl className={classes.formControl} variant="outlined" size="small">
        <InputLabel id="saved-filter-set-label">Saved Filter Sets</InputLabel>
        <Select
          labelId="saved-filter-set-label"
          value={selectedId}
          onChange={handleSelectChange}
          label="Saved Filter Sets"
          disabled={isBusy}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {filterSets.map(fs => (
            <MenuItem key={fs.id} value={fs.id}>
              {fs.name}
              {fs.is_default && <StarIcon className={classes.defaultStar} />}
              {fs.created_by_name && (
                <span className={classes.createdBy}>({fs.created_by_name})</span>
              )}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        size="small"
        variant="contained"
        color="primary"
        onClick={handleApply}
        disabled={isBusy || !selectedFilterSet}
      >
        Apply
      </Button>

      <Tooltip title="Save current filters as a new set">
        <IconButton size="small" onClick={() => setSaveDialogOpen(true)} disabled={isBusy}>
          <SaveIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {selectedFilterSet && (
        <Tooltip title="Update selected set with current filters">
          <IconButton size="small" onClick={handleUpdate} disabled={isBusy}>
            <UpdateIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {selectedFilterSet && (
        <Tooltip title="Delete selected filter set">
          <IconButton size="small" onClick={() => setDeleteDialogOpen(true)} disabled={isBusy}>
            <DeleteIcon fontSize="small" color="error" />
          </IconButton>
        </Tooltip>
      )}

      {isBusy && <CircularProgress size={20} />}

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Save Filter Set</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Save the current filter configuration for quick reuse.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Filter Set Name"
            fullWidth
            variant="outlined"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSave();
            }}
            placeholder="e.g., Standard outreach"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isDefault}
                onChange={e => setIsDefault(e.target.checked)}
                color="primary"
              />
            }
            label="Set as default"
          />
          {errorMessage && (
            <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
              {errorMessage}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} color="primary" variant="contained" disabled={!newName.trim() || isBusy}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Filter Set</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;{selectedFilterSet ? selectedFilterSet.name : ''}&quot;? This cannot be
            undone.
          </Typography>
          {errorMessage && (
            <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
              {errorMessage}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="secondary" variant="contained" disabled={isBusy}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default withStyles(styles)(SavedFilterSetSelector);
